import React, { useEffect } from "react";
import { Button, Dropdown, Menu } from "antd";
import { ArrowRightOutlined, DatabaseOutlined, MessageOutlined, DownOutlined } from "@ant-design/icons";
import { useParams, useLocation } from "react-router-dom";
import { useAiAssistantAccess, useAuth } from "@/hooks/use-auth";
import { registerAiRecordContext, useCurrentAiPageContextStatus, pageContextNotice, refreshCurrentAiPageContext } from "./pageContext";
import { AI_CONTEXT_ACTION_EVENT, CONTEXT_ACTIONS, SCENARIO_ACTIONS, FACILITY_ACTIONS, PAGE_ANALYSIS_ACTIONS, getLaunchContext } from "./contextActions.mjs";
import "./AiContextAction.css";

export default function AiContextAction({ resource = "utility", recordId, disabled = false, facilityAction, pageAnalysis = false }) {
  const { companyId, facilityId } = useParams();
  const { pathname } = useLocation();
  // AI Assistant access: profile permission only, no `role === 1` bypass, so a
  // super admin without the profile grant gets the card hidden too.
  const allowed = useAiAssistantAccess();
  const facilityAllowed = useAuth("facility_view");
  const { context, reason } = useCurrentAiPageContextStatus(companyId, facilityId);
  const kind = facilityAction ? "facility" : "page";
  useEffect(() => {
    if (!allowed || !facilityAllowed || disabled || !recordId || resource === "utility" || facilityAction) return undefined;
    return registerAiRecordContext({ companyId, facilityId, resource, recordId });
  }, [companyId, facilityId, resource, recordId, disabled, allowed, facilityAllowed, facilityAction]);
  const pageId = context?.page_id || ({ dashboard: "page.facility.dashboard", savings: "page.facility.savings", "utility-summary": "page.facility.utility.summary" })[pathname.split("/").filter(Boolean).pop()];
  const primary = facilityAction ? FACILITY_ACTIONS[facilityAction] : (pageAnalysis || resource === "utility" ? PAGE_ANALYSIS_ACTIONS[pageId] || CONTEXT_ACTIONS.utility : CONTEXT_ACTIONS[resource]);
  if (!allowed || !facilityAllowed || !companyId || !facilityId || !primary) return null;
  const ready = !disabled && (facilityAction || (context && (resource === "utility" ? !context.record :
    context.record?.resource === resource && context.record.record_id === Number(recordId))));
  const secondary = facilityAction ? [] : pageId === "page.facility.dashboard" ? [{ ...FACILITY_ACTIONS.daily, kind: "facility" }] : (SCENARIO_ACTIONS[resource] || []).slice(1);
  const launch = (action) => {
    if (!ready) return;
    const actionKind = action.kind || kind;
    const source = getLaunchContext(context, { companyId, facilityId, route: pathname }, actionKind);
    window.dispatchEvent(new CustomEvent(AI_CONTEXT_ACTION_EVENT, { detail: {
      kind: actionKind, prompt: action.prompt, companyId: Number(companyId), facilityId: Number(facilityId),
      route: source.route, recordId: source.record?.record_id || null,
    } }));
  };
  return (
    <section className="facility-ai-context-action" aria-label="AI assistant actions">
      <span className="facility-ai-context-action__icon" aria-hidden="true"><MessageOutlined /></span>
      <span className="facility-ai-context-action__copy">
        <span className="facility-ai-context-action__eyebrow">AI workspace</span>
        <span className="facility-ai-context-action__description">{primary.description}</span>
        <span className="facility-ai-context-action__data-note"><DatabaseOutlined aria-hidden="true" />
          {facilityAction ? "Uses authorized facility data · asks for missing details" : primary.dataNote}
        </span>
        {!facilityAction && pageContextNotice(reason) && <span role="status">{pageContextNotice(reason)}</span>}
      </span>
      <div className="facility-ai-context-action__controls">
        <Button className="facility-ai-context-action__button" type="primary" disabled={!ready} onClick={() => launch(primary)}>
          {primary.label}<ArrowRightOutlined aria-hidden="true" />
        </Button>
        {secondary.length > 0 && <Dropdown trigger={["click"]} overlay={<Menu onClick={({ key }) => launch(secondary.find((action) => action.id === key))} items={secondary.map((action) => ({ key: action.id, label: <span className="facility-ai-scenario-option"><strong>{action.label}</strong><small>{action.description}</small></span> }))} />}>
          <Button disabled={!ready} aria-label="More AI actions">More actions <DownOutlined /></Button>
        </Dropdown>}
        {!facilityAction && ["expired", "error"].includes(reason) && <Button onClick={() => refreshCurrentAiPageContext(companyId, facilityId)}>Refresh page data</Button>}
      </div>
    </section>
  );
}
